import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Edit,
  Trash2,
  MoveRight,
  Lightbulb,
  Bot,
  Users,
  Check
} from 'lucide-react';
import { HighlightRing } from '@/components/shared/SharedTasksSection';
import { format, isSameMonth, isSameDay, isToday } from 'date-fns';
import { useDesign } from '@/contexts/DesignContext';
import { StCard, StAddRow, priorityDot } from '@/components/studio/planner/parts';

function MonthView({
  currentDate,
  selectedDate,
  setSelectedDate,
  monthViewMode,
  setMonthViewMode,
  priorities,
  showAddPriority,
  setShowAddPriority,
  newPriority,
  setNewPriority,
  addPriority,
  togglePriorityComplete,
  deletePriority,
  canManageShared,
  handleEditPriority,
  recommendedPriorities,
  addRecommendedPriority,
  recommendedPrioritiesCollapsed,
  setRecommendedPrioritiesCollapsed,
  calendarDays,
  getTasksForDate,
  getEventsForDate,
  setHoveredItem,
  onNavigateToDay,
  movePriorityToNextMonth,
  shareInfo,
  highlightSharedId,
  // Studio only (106): which half of the two-column month page to render, and the
  // per-day "+" that opens Add task with that date. Undefined in classic.
  studioSlot = null,
  onAddTaskOnDate,
}) {
  const { isStudio } = useDesign();
  // CMD+Enter to submit
  const handlePriorityKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addPriority();
    }
  };

  // Priorities actually belonging to the viewed month. Overdue carry-overs (from
  // prior months, flagged isOverdue) are shown on top but excluded from this
  // month's "X of Y complete" tally.
  const monthPriorities = priorities.filter(p => !p.isOverdue);

  const priorityDialogContent = (
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <Plus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span>Add Monthly Priority</span>
          </DialogTitle>
          <DialogDescription>
            Add a key priority for {format(currentDate, 'MMMM yyyy')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Priority Title</Label>
            <Input
              placeholder="e.g., Plan Easter service setlist"
              value={newPriority.title}
              onChange={(e) => setNewPriority({ ...newPriority, title: e.target.value })}
              onKeyDown={handlePriorityKeyDown}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Description (optional)</Label>
            <Textarea
              placeholder="Add more details..."
              value={newPriority.description}
              onChange={(e) => setNewPriority({ ...newPriority, description: e.target.value })}
              onKeyDown={handlePriorityKeyDown}
            />
          </div>
          {shareInfo?.can_share && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-white/10 px-3 py-2.5">
              <div>
                <Label className="text-sm font-medium">Share with other admins</Label>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  One synced priority for you + {shareInfo.admin_count} other admin{shareInfo.admin_count > 1 ? 's' : ''} — they'll be notified
                </p>
              </div>
              <Switch
                checked={!!newPriority.share}
                onCheckedChange={(v) => setNewPriority({ ...newPriority, share: v })}
                data-testid="share-priority-switch"
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowAddPriority(false)}>Cancel</Button>
          <Button onClick={addPriority} className="bg-indigo-500 hover:bg-indigo-600">Add Priority</Button>
        </DialogFooter>
      </DialogContent>
  );

  // ── Studio (106 §2 Month option B, amended by D1 · 121 §5) ───────────────────
  // D1 (owner 2026-09-23) restores the one control 106 dropped: Month has the
  // same two modes as classic, on the same `monthViewMode` state and the same
  // `setMonthViewMode` handler (no new state, no new prop).
  //   Priorities mode — the main column carries the priorities list and the
  //     Recommended block (two columns, as classic); the rail is the year goals
  //     card alone, so the `priorities` slot renders nothing.
  //   Calendar mode  — unchanged from 106: the Studio month grid in the main
  //     column with the priorities rail beside it.
  // The toggle is drawn in the main column card's title row (the `.st-wr` slot),
  // which is where classic draws it too — classic's toggle sits inside the month
  // Card, above its content. The page title row belongs to PlannerApp.js, which
  // this session does not own; see the question in the report.
  const modeToggle = (
    <div className="st-segc" role="tablist" aria-label="Month view mode">
      <button
        type="button" role="tab" aria-selected={monthViewMode === 'priorities'}
        className={monthViewMode === 'priorities' ? 'st-on' : undefined}
        onClick={() => setMonthViewMode('priorities')}
      >
        <CheckCircle2 /> Priorities
      </button>
      <button
        type="button" role="tab" aria-selected={monthViewMode === 'calendar'}
        className={monthViewMode === 'calendar' ? 'st-on' : undefined}
        onClick={() => setMonthViewMode('calendar')}
      >
        <CalendarIcon /> Calendar
      </button>
    </div>
  );

  // One priority row, shared by the main-column card and the rail card.
  const stRow = (priority) => (
      <div
        key={priority.id}
        className={`st-prow${priority.completed ? ' st-done' : ''}`}
        onClick={() => handleEditPriority(priority)}
        onMouseEnter={() => setHoveredItem?.(
          priority.is_shared
            ? (canManageShared?.(priority) ? { type: 'shared_priority', id: priority.id, data: priority, deleteFn: () => deletePriority(priority.id) } : null)
            : { type: 'priority', id: priority.id, data: priority }
        )}
        onMouseLeave={() => setHoveredItem?.(null)}
      >
        {highlightSharedId === priority.id && <HighlightRing className="rounded-lg" />}
        <button
          type="button"
          className={`st-cb${priority.completed ? ' st-on' : ''}`}
          aria-label={priority.completed ? 'Mark not done' : 'Mark done'}
          onClick={(e) => { e.stopPropagation(); togglePriorityComplete(priority.id, priority.completed); }}
        >
          {priority.completed ? <Check /> : null}
        </button>
        <span className="st-txt">
          <span>{priority.title}</span>
          {priority.is_shared && (
            <span className="st-chip" data-testid={`priority-shared-badge-${priority.id}`}><Users /> Shared</span>
          )}
          {priority.isOverdue && <span className="st-over">Overdue</span>}
        </span>
        <span className="st-time">
          {priority.completed
            ? (priority.is_shared && priority.completed_by_name ? `✓ ${priority.completed_by_name}` : 'Done')
            : ''}
        </span>
        <span className="st-acts">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="st-ibtn" aria-label="Move priority" title="Move priority"
                      onClick={(e) => e.stopPropagation()} data-testid={`move-priority-${priority.id}`}>
                <MoveRight />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); movePriorityToNextMonth?.(priority.id); }}
                data-testid={`move-priority-next-month-${priority.id}`}
              >
                Move to next month
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button type="button" className="st-ibtn" aria-label="Edit priority"
                  onClick={(e) => { e.stopPropagation(); handleEditPriority(priority); }}><Edit /></button>
          <button type="button" className="st-ibtn" aria-label="Delete priority"
                  onClick={(e) => { e.stopPropagation(); deletePriority(priority.id); }}><Trash2 /></button>
        </span>
      </div>
  );
  const stDoneCount = monthPriorities.filter(p => p.completed).length;
  const stPriorityList = (
    priorities.length > 0 ? (
      <div>{priorities.map(stRow)}</div>
    ) : (
      <div className="st-empty">
        <span className="st-glyph"><CheckCircle2 /></span>
        <b>No priorities set for this month</b>
        <span>Add one to set your monthly focus.</span>
      </div>
    )
  );
  const stAddPriorityRow = (
    <StAddRow label="Add a priority" onClick={() => { setNewPriority({ title: '', description: '', share: false }); setShowAddPriority(true); }} />
  );
  // `wide` draws the suggestions in classic's two-column grid (the main column);
  // the 300px rail keeps one column.
  const stRecommended = (wide) => (
    <>
      <button
        type="button"
        className="st-recfold"
        onClick={() => setRecommendedPrioritiesCollapsed(!recommendedPrioritiesCollapsed)}
        aria-expanded={!recommendedPrioritiesCollapsed}
      >
        {recommendedPrioritiesCollapsed ? <Lightbulb /> : <Bot />}
        Recommended priorities
        <span className="st-num" style={{ color: 'var(--st-fg3)' }}>{recommendedPriorities.length}</span>
        {recommendedPrioritiesCollapsed ? <ChevronDown /> : <ChevronUp />}
      </button>
      {!recommendedPrioritiesCollapsed && (
        <div className={wide ? 'st-mrec2' : 'st-sugs'}>
          {recommendedPriorities.map((suggestion, idx) => (
            <button key={idx} type="button" className="st-rec" onClick={() => addRecommendedPriority(suggestion)}>
              <Plus /><span>{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );

  // Priorities mode: the main column is the priorities card; the rail is the
  // year goals card alone (PlannerApp renders it after this slot).
  if (isStudio && studioSlot === 'calendar' && monthViewMode === 'priorities') {
    return (
      <>
        <StCard
          className="st-monthcard"
          tile="planner"
          icon={<CheckCircle2 />}
          title={`${format(currentDate, 'MMMM')} priorities`}
          small={monthPriorities.length > 0 ? `${stDoneCount} of ${monthPriorities.length} done` : undefined}
          right={modeToggle}
        >
          {stPriorityList}
          {stAddPriorityRow}
          {stRecommended(true)}
        </StCard>
        <Dialog open={showAddPriority} onOpenChange={setShowAddPriority}>{priorityDialogContent}</Dialog>
      </>
    );
  }
  if (isStudio && studioSlot === 'priorities' && monthViewMode === 'priorities') return null;

  if (isStudio && studioSlot === 'calendar') {
    const totals = calendarDays.reduce((acc, day) => {
      if (!isSameMonth(day, currentDate)) return acc;
      acc.tasks += getTasksForDate(day).length;
      acc.events += getEventsForDate(day).length;
      return acc;
    }, { tasks: 0, events: 0 });
    return (
      <section className="st-card st-monthcard">
        <div className="st-wh" style={{ paddingBottom: 12 }}>
          <span className="st-tile st-tile-planner"><CalendarIcon /></span>
          <h2 className="st-wt">Calendar<small>{totals.tasks} task{totals.tasks === 1 ? '' : 's'} · {totals.events} event{totals.events === 1 ? '' : 's'}</small></h2>
          <div className="st-wr">{modeToggle}</div>
        </div>
        <div className="st-mcal">
          <div className="st-mh">
            {calendarDays.slice(0, 7).map((d, idx) => <span key={idx}>{format(d, 'EEE')}</span>)}
          </div>
          <div className="st-mg">
            {calendarDays.map((day, idx) => {
              const dayTasks = getTasksForDate(day);
              const dayEvents = getEventsForDate(day);
              const shownEvents = dayEvents.slice(0, 2);
              const shownTasks = dayTasks.slice(0, Math.max(0, 3 - dayEvents.length));
              const hidden = (dayTasks.length + dayEvents.length) - 3;
              return (
                <div
                  key={idx}
                  className={`st-cell${isSameMonth(day, currentDate) ? '' : ' st-out'}${isToday(day) ? ' st-today' : ''}${isSameDay(day, selectedDate) ? ' st-selday' : ''}`}
                  onClick={() => { setSelectedDate(day); if (onNavigateToDay) onNavigateToDay(day); }}
                  data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <span className="st-d">{format(day, 'd')}</span>
                  {onAddTaskOnDate && (
                    <button
                      type="button"
                      className="st-dayplus"
                      aria-label={`Add a task on ${format(day, 'EEEE, MMMM d')}`}
                      title="Add a task on this day"
                      onClick={(e) => { e.stopPropagation(); onAddTaskOnDate(day); }}
                    >
                      <Plus />
                    </button>
                  )}
                  {shownEvents.map(event => (
                    <div key={event.id} className="st-it st-cal" title={event.title}><span>{event.title}</span></div>
                  ))}
                  {shownTasks.map(task => (
                    <div key={task.id} className={`st-it${task.completed ? ' st-done' : ''}`} title={task.title}>
                      <span className="st-pd" style={{ background: priorityDot(task.priority) }} />
                      <span>{task.title}</span>
                    </div>
                  ))}
                  {hidden > 0 && <div className="st-more">+{hidden} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (isStudio && studioSlot === 'priorities') {
    return (
      <>
        <StCard
          tile="planner"
          icon={<CheckCircle2 />}
          title={`${format(currentDate, 'MMMM')} priorities`}
          small={monthPriorities.length > 0 ? `${stDoneCount} of ${monthPriorities.length} done` : undefined}
        >
          {stPriorityList}
          {stAddPriorityRow}
          {stRecommended(false)}
        </StCard>
        <Dialog open={showAddPriority} onOpenChange={setShowAddPriority}>{priorityDialogContent}</Dialog>
      </>
    );
  }

  return (
    <>
      {/* Toggle between Priorities and Calendar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex gap-2">
          <Button 
            variant={monthViewMode === 'priorities' ? 'secondary' : 'ghost'} 
            size="sm"
            onClick={() => setMonthViewMode('priorities')}
            className={`text-xs sm:text-sm ${monthViewMode === 'priorities' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' : ''}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" /> Priorities
          </Button>
          <Button 
            variant={monthViewMode === 'calendar' ? 'secondary' : 'ghost'} 
            size="sm"
            onClick={() => setMonthViewMode('calendar')}
            className={`text-xs sm:text-sm ${monthViewMode === 'calendar' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' : ''}`}
          >
            <CalendarIcon className="w-4 h-4 mr-1" /> Calendar
          </Button>
        </div>
        {monthViewMode === 'priorities' && (
          <Dialog open={showAddPriority} onOpenChange={setShowAddPriority}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600 w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-1" /> Add Priority
              </Button>
            </DialogTrigger>
            {priorityDialogContent}
          </Dialog>
        )}
      </div>

      {/* Priorities View */}
      {monthViewMode === 'priorities' && (
        <div className="space-y-4">
          <div className="text-center py-2">
            <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{format(currentDate, 'MMMM yyyy')}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Monthly Priorities
              {monthPriorities.length > 0 && (
                <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
                  {monthPriorities.filter(p => p.completed).length} of {monthPriorities.length} complete
                </span>
              )}
            </p>
          </div>
          
          <div className="space-y-2">
            {priorities.length > 0 ? priorities.map((priority) => (
              <div
                key={priority.id}
                className={`relative group flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-all cursor-pointer ${
                  priority.isOverdue && !priority.completed
                    ? 'bg-amber-50 dark:bg-amber-500/15 border-amber-200 dark:border-amber-500/40 hover:border-amber-300 dark:hover:border-amber-400/60 hover:shadow-md hover:-translate-y-px'
                    : priority.completed
                      ? 'bg-elevated border-slate-200 dark:border-white/[0.08] opacity-70'
                      : 'bg-elevated border-slate-200 dark:border-white/[0.08] hover:border-indigo-200 dark:hover:border-indigo-400/40 hover:shadow-md hover:-translate-y-px dark:hover:bg-[#3a3d41]'
                }`}
                onClick={() => handleEditPriority(priority)}
                onMouseEnter={() => setHoveredItem?.(
                  priority.is_shared
                    // Shared priorities live in Mongo: route Delete through deletePriority
                    // (useSharedItems), and only for people the backend lets delete.
                    ? (canManageShared?.(priority) ? { type: 'shared_priority', id: priority.id, data: priority, deleteFn: () => deletePriority(priority.id) } : null)
                    : { type: 'priority', id: priority.id, data: priority }
                )}
                onMouseLeave={() => setHoveredItem?.(null)}
              >
                {highlightSharedId === priority.id && <HighlightRing className="rounded-xl" />}
                <Checkbox
                  checked={priority.completed}
                  onCheckedChange={() => togglePriorityComplete(priority.id, priority.completed)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm sm:text-base flex items-center gap-2 ${priority.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'}`}>
                    <span className="truncate">{priority.title}</span>
                    {priority.is_shared && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-300 font-medium flex-shrink-0" data-testid={`priority-shared-badge-${priority.id}`}>
                        <Users className="w-2.5 h-2.5" /> Shared
                      </span>
                    )}
                    {priority.is_shared && priority.completed && priority.completed_by_name && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex-shrink-0">✓ {priority.completed_by_name}</span>
                    )}
                    {priority.isOverdue && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-500/25 text-amber-800 dark:text-amber-200 font-medium flex-shrink-0">
                        Overdue
                      </span>
                    )}
                  </div>
                  {priority.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{priority.description}</p>
                  )}
                </div>
                <div className="flex gap-0.5 flex-shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                        title="Move priority"
                        data-testid={`move-priority-${priority.id}`}
                      >
                        <MoveRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-indigo-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); movePriorityToNextMonth?.(priority.id); }}
                        data-testid={`move-priority-next-month-${priority.id}`}
                      >
                        Move to next month
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleEditPriority(priority); }}>
                    <Edit className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-indigo-500" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deletePriority(priority.id); }}>
                    <Trash2 className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 hover:text-red-500" />
                  </Button>
                </div>
              </div>
            )) : (
              <div className="text-center py-12 text-slate-400 dark:text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-lg font-medium">No priorities set for this month</p>
                <p className="text-sm">Click &quot;Add Priority&quot; to set your monthly focus</p>
              </div>
            )}
          </div>

          {/* Recommended Priorities - Collapsible */}
          <div className="mt-6">
            {recommendedPrioritiesCollapsed ? (
              <button
                onClick={() => setRecommendedPrioritiesCollapsed(false)}
                className="w-full p-3 rounded-xl border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/40 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100/60 dark:hover:bg-indigo-500/15 transition-all flex items-center justify-center gap-2"
              >
                <Lightbulb className="w-4 h-4" />
                <span className="text-sm font-medium">Show Recommended Priorities</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <Card className="shadow-sm border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/30 dark:bg-indigo-500/10">
                <CardHeader className="pb-3 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-indigo-900 dark:text-indigo-200">
                      <Bot className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                      Recommended Priorities
                    </CardTitle>
                    <button
                      onClick={() => setRecommendedPrioritiesCollapsed(true)}
                      className="p-1.5 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
                    >
                      <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                    </button>
                  </div>
                  <CardDescription className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                    Common priorities for worship leaders
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {recommendedPriorities.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => addRecommendedPriority(suggestion)}
                        className="w-full text-left p-2.5 rounded-lg bg-card border border-slate-100 dark:border-white/10 hover:border-indigo-200 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors text-sm text-slate-700 dark:text-slate-200"
                      >
                        <Plus className="w-4 h-4 inline mr-2 text-indigo-500" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Calendar View */}
      {monthViewMode === 'calendar' && (
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {/* Header follows the grid's first row so "start weeks on Monday" rotates it (2026-09-07) */}
          {calendarDays.slice(0, 7).map((d, idx) => (
            <div key={idx} className="text-center text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 py-1 sm:py-2">
              <span className="sm:hidden">{format(d, 'EEEEE')}</span>
              <span className="hidden sm:inline">{format(d, 'EEE')}</span>
            </div>
          ))}
          {calendarDays.map((day, idx) => {
            const dayTasks = getTasksForDate(day);
            const dayEvents = getEventsForDate(day);
            const isSelected = isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const hasTasks = dayTasks.length > 0 || dayEvents.length > 0;
            const dayOfWeek = day.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isPast = day < new Date(new Date().setHours(0,0,0,0)) && !isToday(day);
            
            return (
              <div
                key={idx}
                onClick={() => {
                  setSelectedDate(day);
                  if (onNavigateToDay) onNavigateToDay(day);
                }}
                className={`
                  min-h-[48px] sm:min-h-[100px] p-1 sm:p-2 rounded-lg cursor-pointer transition-all
                  ${isSelected ? 'bg-indigo-100 dark:bg-indigo-900/50 ring-2 ring-indigo-500 dark:ring-indigo-400' : 'hover:bg-indigo-50/50 dark:hover:bg-indigo-500/15'}
                  ${!isCurrentMonth ? 'opacity-30' : ''}
                  ${isToday(day) ? 'bg-indigo-50 dark:bg-indigo-950/40' : isWeekend && isCurrentMonth ? 'bg-slate-50/60 dark:bg-white/[0.02]' : ''}
                `}
                data-testid={`cal-day-${format(day, 'yyyy-MM-dd')}`}
              >
                <div className={`text-xs sm:text-sm font-medium text-center sm:text-left ${
                  isToday(day) ? 'text-indigo-600 dark:text-indigo-400 font-bold' : isPast ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'
                }`}>
                  {format(day, 'd')}
                </div>
                {/* Mobile: dot indicator */}
                {hasTasks && (
                  <div className="sm:hidden flex justify-center mt-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${dayTasks.some(t => !t.completed) || dayEvents.length > 0 ? 'bg-indigo-500' : 'bg-green-500'}`} />
                  </div>
                )}
                {/* Desktop: task/event previews */}
                <div className="hidden sm:block mt-1 space-y-0.5">
                  {dayEvents.slice(0, 2).map(event => (
                    <div
                      key={event.id}
                      className="text-[10px] truncate px-1 py-0.5 rounded flex items-center gap-1"
                      style={{ backgroundColor: `${event.color}20`, color: event.color }}
                    >
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
                      {event.title}
                    </div>
                  ))}
                  {dayTasks.slice(0, Math.max(0, 3 - dayEvents.length)).map(task => (
                    <div
                      key={task.id}
                      className={`text-[10px] truncate px-1 py-0.5 rounded ${task.completed ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 line-through' : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'}`}
                    >
                      {task.title}
                    </div>
                  ))}
                  {(dayTasks.length + dayEvents.length) > 3 && (
                    <div className="text-[10px] text-slate-400 dark:text-slate-500">+{dayTasks.length + dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default MonthView;
